import { colors, responsiveFontSizes } from "@mui/material";
import { enUS, esES, frFR } from "@mui/material/locale";
import { createTheme as createMuiTheme } from "@mui/material/styles";
import type {
    Palette as MuiPalette,
    TypeBackground as MuiTypeBackground,
} from "@mui/material/styles/createPalette";
import type { Theme as MuiTheme } from "@mui/material/styles/createTheme";
import type { Shadows as MuiShadows } from "@mui/material/styles/shadows";
import _ from "lodash";
import { getLocale } from "src/i18n";
import { LanguageEnum } from "src/types/enum/LanguageEnum";
import { constants, type Constants } from "./constants";
import { colors as customColors, customPalette } from "./customPalette";
import { softShadows } from "./shadows";
import typography from "./typography";

interface TypeBackground extends MuiTypeBackground {
    dark: string;
}

interface Palette extends MuiPalette {
    background: TypeBackground;
}

export interface Theme extends MuiTheme {
    name: string;
    palette: Palette;
}

type Direction = "ltr" | "rtl";

interface ThemeConfig {
    direction?: Direction;
    responsiveFontSizes?: boolean;
    theme?: string;
    components?: Record<string, any>;
}

interface ThemeOptions {
    name?: string;
    direction?: Direction;
    typography?: Record<string, any>;
    components?: Record<string, any>;
    palette?: Record<string, any> & typeof customPalette;
    shadows?: MuiShadows;
    constants?: Constants;
}

const MUI_LANGS = {
    [LanguageEnum.FR]: frFR,
    [LanguageEnum.GB]: enUS,
    [LanguageEnum.ES]: esES,
};
const baseOptions: ThemeOptions = {
    direction: "ltr",
    typography,
    components: {
        MuiTypography: {
            defaultProps: {
                variant: "body",
                variantMapping: {
                    h1Medium: "h1",
                    h1SemiBold: "h1",
                    h1Bold: "h1",
                    h2Medium: "h2",
                    h2SemiBold: "h2",
                    h2Bold: "h2",
                    h3Medium: "h3",
                    h3SemiBold: "h3",
                    h3Bold: "h3",
                    subtitle: "p",
                    subtitleMedium: "p",
                    subtitleSemiBold: "p",
                    subtitleBold: "p",
                    cta: "span",
                    ctaMedium: "span",
                    ctaSemiBold: "span",
                    ctaBold: "span",
                    body: "p",
                    bodyMedium: "p",
                    bodySemiBold: "p",
                    bodyBold: "p",
                    caption: "span",
                    captionMedium: "span",
                    captionSemiBold: "span",
                    captionBold: "span",
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    boxShadow: "0px 8px 14px rgba(57, 77, 255, 0.02)",
                },
            },
        },
        MuiAccordion: {
            styleOverrides: {
                root: {
                    boxShadow:
                        "0 0 0 1px rgba(63, 63, 68, 0.05), 0 1px 2px 0 rgba(63, 63, 68, 0.15)",
                },
            },
        },
        MuiAutocomplete: {
            styleOverrides: {
                popper: {
                    boxShadow:
                        "0 0 0 1px rgba(63, 63, 68, 0.05), 0 1px 2px 0 rgba(63, 63, 68, 0.15)",
                },
            },
        },
        MuiLinearProgress: {
            styleOverrides: {
                root: {
                    borderRadius: 3,
                    overflow: "hidden",
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    textTransform: "none",
                },
            },
        },
        MuiTab: {
            styleOverrides: {
                root: {
                    fontSize: 15,
                    textTransform: "none",
                },
            },
        },
        MuiListItemIcon: {
            styleOverrides: {
                root: {
                    minWidth: 32,
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    fontSize: 13,
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    backgroundColor: "rgba(0,0,0,0.075)",
                },
            },
        },
        MuiDialog: {
            defaultProps: {
                fullWidth: true,
                maxWidth: "md",
            },
            // Override MUI breakpoints for Dialog maxWidth to match our design breakpoints, can be removed if the breakpoints are updated
            styleOverrides: {
                paper: {
                    maxHeight: "calc(100vh - 200px)",
                    padding: 24,
                },
                paperWidthXs: {
                    maxWidth: 320,
                },
                paperWidthSm: {
                    maxWidth: 480,
                },
                paperWidthMd: {
                    maxWidth: 720,
                },
                paperWidthLg: {
                    maxWidth: 960,
                },
                paperWidthXl: {
                    maxWidth: 1200,
                },
            },
        },
        MuiDialogTitle: {
            styleOverrides: {
                root: {
                    padding: 0,
                },
            },
        },
        MuiDialogContent: {
            styleOverrides: {
                root: {
                    padding: 0,
                    display: "flex",
                    flexDirection: "column",
                    gap: 24,
                    "&:first-of-type": {
                        paddingTop: 24,
                    },
                    overflowY: "auto",
                },
            },
        },
        MuiDialogActions: {
            styleOverrides: {
                root: {
                    padding: 0,
                    marginTop: 24,
                },
            },
        },
        MuiTableSortLabel: {
            styleOverrides: {
                icon: {
                    marginLeft: "0.5px",
                    marginRight: "1px",
                },
            },
        },
    },
};

const themesOptions: ThemeOptions[] = [
    {
        name: "LIGHT",
        components: {
            MuiTableCell: {
                sizeSmall: {
                    padding: "6px 6px 6px 6px",
                },
            },
            MuiInputBase: {
                input: {
                    "&::placeholder": {
                        opacity: 1,
                        color: colors.blueGrey[600],
                    },
                },
            },
            MuiInputLabel: {
                root: {
                    "&$focused": {
                        color: "#4A90E2",
                    },
                },
            },
            MuiOutlinedInput: {
                root: {
                    height: 50,
                    position: "relative",
                    "& $notchedOutline": {
                        borderColor: colors.blueGrey[600],
                    },
                    "&:hover:not($disabled):not($focused):not($error) $notchedOutline": {
                        borderColor: "#4A90E2",
                        // Reset on touch devices, it doesn't add specificity
                        "@media (hover: none)": {
                            borderColor: "rgba(0, 0, 0, 0.23)",
                        },
                    },
                    "&$focused $notchedOutline": {
                        borderColor: "#4A90E2",
                        borderWidth: 1,
                    },
                },
            },
        },
        palette: {
            type: "light",
            action: {
                active: customColors.grey["500"],
            },
            background: {
                default: "#F5FAFE",
                dark: "#F5FAFE",
                paper: colors.common.white,
            },
            text: {
                primary: customColors.grey["500"],
                secondary: customColors.grey["500"],
            },
            success: {
                ...customColors.success,
                main: customColors.success["500"],
            },
            grey: {
                ...customColors.grey,
                main: customColors.grey["500"],
            },
            ...customPalette,
        },
        constants: constants,
        shadows: softShadows,
    },
];

export const createTheme = (config: ThemeConfig = {}): Theme => {
    let themeOptions = themesOptions.find((theme) => theme.name === config.theme);

    if (!themeOptions) {
        // biome-ignore lint/suspicious/noConsole: legacy
        console.warn(new Error(`The theme ${config.theme} is not valid`));
        [themeOptions] = themesOptions;
    }

    const locale = getLocale();

    let theme = createMuiTheme(
        _.merge({}, baseOptions, themeOptions, {
            direction: config.direction,
        }),
        MUI_LANGS[locale]
    );

    if (config.responsiveFontSizes) {
        theme = responsiveFontSizes(theme);
    }

    return theme as Theme;
};
